import { format } from 'date-fns';

// ANCHOR: referral-reward-copy — single source of truth for what a teacher is told they
// received, for both the referee modal and the referrer-side surfaces that share
// ReferralRewardDetail.
//
// `trialEndDateIso` is the *account's* premium end date, not a value carried on the grant:
// the backend applies trial days by moving user_info.trial_end_date (_async_apply_reward in
// referral_grant_controller.py), and "you have access until X" is a claim about the account
// rather than about this one grant. Callers pass it only when they know it already reflects
// the grant — RefereeRewardModal refreshes the user first. Leaving it out falls back to the
// day-count wording, which is never wrong, just vaguer.

export const formatPremiumUntil = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMMM do, yyyy');
};

const days = (amount) => `${amount} free day${Number(amount) === 1 ? '' : 's'}`;
const dayCount = (amount) => `${amount} day${Number(amount) === 1 ? '' : 's'}`;

/**
 * What the teacher got, as one sentence.
 *
 * One sentence rather than a headline/detail pair: the referee modal renders this in its
 * subtitle slot, directly under "A colleague sent you a gift!", where a second line of
 * supporting text would just crowd the thing the teacher actually needs to read.
 *
 * Returns null for a reward we can't describe — a grant with an empty reward_json, or a
 * reward_type added to the backend that this build doesn't know about yet. Callers render
 * nothing rather than an empty box.
 */
export const getRewardDescription = ({ rewardType, amount, payload, trialEndDateIso } = {}) => {
  const until = formatPremiumUntil(trialEndDateIso);

  if (rewardType === 'trial_days') {
    if (until) return `You have free access to Diffit Premium until ${until}.`;
    if (!amount) return null;
    return `You have ${days(amount)} of Diffit Premium.`;
  }

  if (rewardType === 'promo_code') {
    const code = payload?.code;
    // Already redeemed on their behalf, so this reports what happened rather than handing
    // them a code to go and enter. Once we can state the resulting date, that IS what
    // happened — the code is bookkeeping the teacher has nothing to do with, so it drops.
    if (until) return `You have free access to Diffit Premium until ${until}.`;
    if (!code) return null;
    return `We applied the code ${code} to your account.`;
  }

  if (rewardType === 'swag_credit') {
    if (!amount) return null;
    return `You have $${Number(amount).toFixed(2)} to spend in the Diffit swag store.`;
  }

  if (rewardType === 'template') {
    if (!payload?.template_url) return null;
    // The button lives in ReferralRewardDetail; this is just the line above it.
    return 'A set of ready-to-use templates is yours to keep.';
  }

  return null;
};

// ── Referee modal ────────────────────────────────────────────────────────
// The referee modal names the benefit in its title, so it needs a title/subtitle pair rather
// than the one standalone sentence above. The subtitle completes the title instead of
// repeating its noun ("You've got Diffit Premium" / "Free until <date>"), and carries the
// attribution — a teacher who followed a forwarded link or a social post may have no idea a
// reward was coming, and the title alone doesn't tell them where it came from.
//
// Deliberately not "a colleague": ref_src can be social_twitter, social_facebook or
// social_pinterest (constants/linkSources.js), and the invite email is explicitly built to
// survive forwarding, so the sender is often not a colleague of theirs in any real sense.

// The title carries the whole benefit, date included, so a single-reward modal needs no
// second line for it — the subtitle is free to do nothing but credit the sharer. Returns
// null for a reward it can't describe, which is how the caller decides not to open at all.
const refereeTitle = ({ rewardType, amount, payload, trialEndDateIso }) => {
  const until = formatPremiumUntil(trialEndDateIso);

  if (rewardType === 'trial_days') {
    if (until) return `You've got Diffit Premium until ${until}`;
    // No date means the days were never applied (an already-paid referee) or the user object
    // predates the grant. Say what was awarded rather than name a date that owes it nothing.
    if (!amount) return null;
    return `You've got ${dayCount(amount)} of Diffit Premium`;
  }
  if (rewardType === 'promo_code') {
    if (until) return `You've got Diffit Premium until ${until}`;
    // Without a date all we know is that a code landed; claiming a benefit would be a guess.
    if (!payload?.code) return null;
    return "You've got Diffit Premium";
  }
  if (rewardType === 'swag_credit') {
    if (!amount) return null;
    return `You've got $${Number(amount).toFixed(2)} in swag credit`;
  }
  if (rewardType === 'template') {
    if (!payload?.template_url) return null;
    return "You've got a set of Diffit templates";
  }
  return null;
};

// Two rewards can be different types, and stacking both benefits into one title reads worse
// than naming neither. It takes two programs paying the same referee, so it isn't worth
// combinatorial copy — the subtitle lists each one instead.
const MULTI_TITLE = "You've got a few things from Diffit";

// Named when the backend could resolve one (GET /referral/me/rewards/unseen joins the
// referral to its referrer), anonymous when it couldn't. A referrer with no given_name or
// name set stays anonymous rather than being identified by email — see
// _async_referrer_names_for_grants. The anonymous form drops the call to action, because
// "make sure to thank the teacher who shared Diffit with you" asks for something the
// teacher has no way to do.
const thankYou = (referrerName) =>
  referrerName
    ? `Make sure to thank ${referrerName} for sharing Diffit with you!`
    : 'Thanks to the teacher who shared Diffit with you.';

// Display names are user-controlled free text. Past this length it isn't a name any more,
// and it would wrap the subtitle into a paragraph — so fall back to the anonymous wording.
const MAX_REFERRER_NAME = 40;

// Every grant in the list carries the same referrer (a referee has exactly one referral), so
// the first usable one answers for all of them.
const resolveReferrerName = (rewards) => {
  const found = rewards.map((r) => (r.referrer_name || '').trim()).find(Boolean);
  return found && found.length <= MAX_REFERRER_NAME ? found : null;
};

// Only reached when one modal has to describe two rewards at once, where the title has gone
// neutral. No trailing period: these get joined into a sentence.
const refereeLine = ({ rewardType, amount, payload, trialEndDateIso }) => {
  const until = formatPremiumUntil(trialEndDateIso);

  if (rewardType === 'trial_days') {
    if (until) return `Diffit Premium until ${until}`;
    return amount ? `Diffit Premium for ${dayCount(amount)}` : null;
  }
  if (rewardType === 'promo_code') {
    if (until) return `Diffit Premium until ${until}`;
    return payload?.code ? `the code ${payload.code}, applied to your account` : null;
  }
  if (rewardType === 'swag_credit') {
    return amount ? `$${Number(amount).toFixed(2)} in Diffit swag credit` : null;
  }
  // 'template' has no line: the link below speaks for itself.
  return null;
};

/**
 * Title and subtitle for the referee reward modal.
 *
 * Returns null when there is nothing describable to say — an unrecognised reward_type, or a
 * grant with an empty reward_json — so the modal can decline to open rather than show a
 * title over an empty space.
 */
export const getRefereeRewardCopy = (rewards = [], { trialEndDateIso = null } = {}) => {
  if (rewards.length === 0) return null;

  const described = rewards
    .map((reward) => {
      const parts = {
        rewardType: reward.reward_type,
        amount: reward.amount,
        payload: reward.payload,
        trialEndDateIso,
      };
      return { title: refereeTitle(parts), line: refereeLine(parts) };
    })
    .filter((entry) => entry.title);

  if (described.length === 0) return null;

  const credit = thankYou(resolveReferrerName(rewards));

  // Deduped, not counted: a trial_days and a promo_code grant that both resolve to the same
  // date describe one benefit, and saying it once beats falling back to the neutral title.
  const titles = [...new Set(described.map((entry) => entry.title))];
  if (titles.length === 1) return { title: titles[0], subtext: credit };

  const lines = described.map((entry) => entry.line).filter(Boolean);
  return {
    title: MULTI_TITLE,
    subtext: lines.length ? `You've got ${lines.join(' and ')}. ${credit}` : credit,
  };
};
